import React, { useState, useEffect } from 'react';
import { X, Save, Eye, EyeOff, Upload, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { DynamicQRRow } from '../../services/sbDynamicQR';
import { 
  generateCustomizedDynamicQR, 
  fileToDataUrl, 
  validateImageFile,
  DynamicQRCustomization,
  GeneratedQRResult
} from '../../utils/dynamicQRHelpers';

interface DynamicQREditModalProps {
  qrCode: DynamicQRRow;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedData: any) => void;
}

export const DynamicQREditModal: React.FC<DynamicQREditModalProps> = ({
  qrCode,
  isOpen,
  onClose,
  onSave
}) => {
  const [formData, setFormData] = useState({
    originalUrl: qrCode.original_url,
    title: qrCode.title,
    description: qrCode.description || '',
    expiresAt: qrCode.expires_at ? new Date(qrCode.expires_at).toISOString().slice(0, 16) : '',
    password: '',
    isActive: qrCode.is_active,
  });

  const [customization, setCustomization] = useState<DynamicQRCustomization>({
    size: 300,
    foregroundColor: '#000000',
    backgroundColor: '#ffffff',
    errorCorrectionLevel: 'M',
    transparentBackground: false,
    logoFile: null,
    logoDataUrl: null,
    margin: 4,
    ...qrCode.customization
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewQR, setPreviewQR] = useState<GeneratedQRResult | null>(null);

  // Initialize customization from existing QR code
  useEffect(() => {
    if (qrCode.customization) {
      setCustomization(prev => ({
        ...prev,
        ...qrCode.customization
      }));
    }
  }, [qrCode]);

  // Generate preview when URL or customization changes
  useEffect(() => {
    const updatePreview = async () => {
      if (!formData.originalUrl) return;

      try {
        const result = await generateCustomizedDynamicQR(formData.originalUrl, customization);
        setPreviewQR(result);
      } catch (err) {
        console.error('Preview generation error:', err);
      }
    };

    const timeoutId = setTimeout(updatePreview, 300);
    return () => clearTimeout(timeoutId);
  }, [formData.originalUrl, customization]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleCustomizationChange = (field: keyof DynamicQRCustomization, value: any) => {
    setCustomization(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    try {
      const dataUrl = await fileToDataUrl(file);
      setCustomization(prev => ({
        ...prev,
        logoFile: file,
        logoDataUrl: dataUrl,
        errorCorrectionLevel: prev.errorCorrectionLevel === 'L' ? 'M' : prev.errorCorrectionLevel
      }));
      setError(null);
    } catch (err) {
      setError('Failed to process logo file');
    }
  };

  const removeLogo = () => {
    setCustomization(prev => ({
      ...prev,
      logoFile: null,
      logoDataUrl: null
    }));
  };

  const handleSave = async () => {
    if (!formData.originalUrl.trim()) {
      setError('URL is required');
      return;
    }

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const updatedData = {
        originalUrl: formData.originalUrl,
        title: formData.title,
        description: formData.description || undefined,
        expiresAt: formData.expiresAt || undefined,
        password: formData.password || undefined,
        isActive: formData.isActive,
        customization: customization
      };

      onSave(updatedData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update QR code');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">Edit Dynamic QR Code</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form Section */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Destination URL *</label>
              <Input
                type="url"
                value={formData.originalUrl}
                onChange={(e) => handleInputChange('originalUrl', e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <Input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="My QR Code"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional description"
                className="w-full p-2 border border-input rounded-md bg-background min-h-[80px] resize-none"
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Expiration Date</label>
              <Input
                type="datetime-local"
                value={formData.expiresAt}
                onChange={(e) => handleInputChange('expiresAt', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password Protection</label>
              <div className="flex space-x-2">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Optional password"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="isActive" className="text-sm font-medium">
                QR Code is Active
              </label>
            </div>

            {/* Customization Toggle */}
            <Button
              variant="outline"
              onClick={() => setShowCustomization(!showCustomization)}
              className="w-full"
            >
              <Palette className="h-4 w-4 mr-2" />
              {showCustomization ? 'Hide' : 'Show'} QR Customization
            </Button>

            {showCustomization && (
              <div className="space-y-4 p-4 border rounded-md bg-muted/50">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Logo (Optional)</label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload-edit"
                    />
                    <label
                      htmlFor="logo-upload-edit"
                      className="flex items-center space-x-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-accent transition-colors"
                    >
                      <Upload className="h-4 w-4" />
                      <span>Upload Logo</span>
                    </label>
                    {customization.logoFile && (
                      <Button variant="outline" size="sm" onClick={removeLogo}>
                        Remove Logo
                      </Button>
                    )}
                  </div>
                  {customization.logoDataUrl && (
                    <div className="mt-2">
                      <img
                        src={customization.logoDataUrl}
                        alt="Logo preview"
                        className="w-16 h-16 object-contain border rounded"
                      />
                    </div>
                  )}
                </div>

                {/* Size and Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Size</label>
                    <input
                      type="range"
                      min="200"
                      max="800"
                      value={customization.size}
                      onChange={(e) => handleCustomizationChange('size', parseInt(e.target.value))}
                      className="w-full"
                    />
                    <span className="text-sm text-muted-foreground">{customization.size}px</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Error Correction</label>
                    <select
                      value={customization.errorCorrectionLevel}
                      onChange={(e) => handleCustomizationChange('errorCorrectionLevel', e.target.value)}
                      className="w-full p-2 border border-input rounded-md bg-background"
                    >
                      <option value="L">Low (7%)</option>
                      <option value="M">Medium (15%)</option>
                      <option value="Q">Quartile (25%)</option>
                      <option value="H">High (30%)</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Foreground Color</label>
                    <input
                      type="color"
                      value={customization.foregroundColor}
                      onChange={(e) => handleCustomizationChange('foregroundColor', e.target.value)}
                      className="w-full h-10 border border-input rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Background Color</label>
                    <input
                      type="color"
                      value={customization.backgroundColor}
                      onChange={(e) => handleCustomizationChange('backgroundColor', e.target.value)}
                      className="w-full h-10 border border-input rounded-md"
                      disabled={customization.transparentBackground}
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="transparent-edit"
                    checked={customization.transparentBackground}
                    onChange={(e) => handleCustomizationChange('transparentBackground', e.target.checked)}
                    className="w-4 h-4"
                  />
                  <label htmlFor="transparent-edit" className="text-sm font-medium">
                    Transparent Background
                  </label>
                </div>
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Preview Section */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Live Preview</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-center space-y-4">
                {previewQR ? (
                  <div className="bg-white p-4 rounded-lg shadow-sm border-2 border-dashed border-gray-300">
                    <img
                      src={previewQR.pngDataUrl}
                      alt="QR Code Preview"
                      className="max-w-full h-auto"
                      style={{ maxWidth: '250px' }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 text-muted-foreground">
                    <p>Enter URL to see preview</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-end space-x-4 p-6 border-t">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            <Save className="h-4 w-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
